import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { routePath } from '../../../utils/urls'
import { getRouteFacts, isTier1Route, titleCase } from '../../../utils/routeData'

/**
 * Animated route showcase under the homepage h1.
 *
 * Two marquee rows of route cards in the brand palette, each with a taxi that
 * drives across the card on hover, and a coral cab travelling the divider
 * road. Pure CSS animation - no timers - and it stops for
 * prefers-reduced-motion.
 *
 * Routes that exist as prerendered Tier-1 pages render as real <Link>s with
 * their genuine distance/fare from routeFacts. Routes people ask for that we
 * cannot yet publish (Mumbai has no CMS page) prefill the booking form
 * instead - same behaviour as the PopularRoutes cards - so the homepage never
 * links a crawler to a thin page.
 */

// Order mixes regions so both rows look varied. [from, to]
const ROW_ONE = [
  ['mumbai', 'ahmedabad'],
  ['delhi', 'jaipur'],
  ['surat', 'ahmedabad'],
  ['pune', 'mumbai'],
  ['delhi', 'agra'],
  ['jaipur', 'udaipur'],
  ['delhi', 'chandigarh'],
  ['ahmedabad', 'udaipur'],
]
const ROW_TWO = [
  ['mumbai', 'pune'],
  ['bengaluru', 'mysore'],
  ['chennai', 'tirupati'],
  ['pune', 'nashik'],
  ['lucknow', 'varanasi'],
  ['indore', 'bhopal'],
  ['kolkata', 'puri'],
  ['delhi', 'dehradun'],
]

const Taxi = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 48 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M6 16c0-1 .6-4 2-5l3-1 4-5c.5-.6 1.2-1 2-1h10c.8 0 1.5.4 2 1l4 5 5 1.5c1.2.4 2 1.5 2 2.8V16a2 2 0 0 1-2 2h-2"
      fill="currentColor"
    />
    <path d="M8 18H6a2 2 0 0 1-2-2v-1" fill="none" stroke="currentColor" strokeWidth="2" />
    <rect x="16" y="1" width="7" height="3" rx="1" fill="currentColor" />
    <path d="M17 6h5l2.5 4H14.5L17 6zm9 0h2.5l3 4H26V6z" fill="#fff" opacity=".85" />
    <circle cx="13" cy="18" r="4" fill="#0B1F3A" stroke="#fff" strokeWidth="1.5" />
    <circle cx="35" cy="18" r="4" fill="#0B1F3A" stroke="#fff" strokeWidth="1.5" />
  </svg>
)

const RouteCard = ({ from, to }) => {
  const navigate = useNavigate()
  const fromName = titleCase(from)
  const toName = titleCase(to)
  const tier1 = isTier1Route(from, to)
  const facts = getRouteFacts(from, to)
  const label = `${fromName} to ${toName} cab`

  const body = (
    <>
      <div className="route-showcase__cities">
        <span className="route-showcase__city">{fromName}</span>
        {/* The dashed track stays — it reads as "from here to there". The taxi
            that used to drive along it was removed on request: at card size it
            sat on top of the city names rather than between them. */}
        <span className="route-showcase__track" aria-hidden="true" />
        <span className="route-showcase__city">{toName}</span>
      </div>
      <div className="route-showcase__meta">
        {facts?.distanceKm ? (
          <>
            <span>{facts.distanceKm} km</span>
            {facts.duration && <span>~{facts.duration}</span>}
            {facts.startingPrice && (
              <span className="route-showcase__price">from {facts.startingPrice}</span>
            )}
          </>
        ) : (
          <span className="route-showcase__price">Book one way</span>
        )}
      </div>
    </>
  )

  // Tier-1 pages exist as real HTML - link them. Others prefill the form.
  if (tier1) {
    return (
      <Link className="route-showcase__card" to={routePath(from, to)} title={label}>
        {body}
      </Link>
    )
  }
  return (
    <button
      type="button"
      className="route-showcase__card"
      title={label}
      onClick={() => {
        navigate('/', { state: { routePrefill: { fromCity: from, toCity: to } } })
        setTimeout(() => {
          document.querySelector('.home-main')?.scrollIntoView({ behavior: 'smooth' })
        }, 50)
      }}
    >
      {body}
    </button>
  )
}

// Cards are duplicated once so the 50% translate loop is seamless.
const MarqueeRow = ({ pairs, reverse }) => (
  <div className={`route-showcase__row ${reverse ? 'route-showcase__row--reverse' : ''}`}>
    <div className="route-showcase__belt">
      {[...pairs, ...pairs].map(([from, to], i) => (
        <RouteCard key={`${from}-${to}-${i}`} from={from} to={to} />
      ))}
    </div>
  </div>
)

const RouteShowcase = ({ heading, subheading }) => (
  <section className="route-showcase" aria-labelledby="route-showcase-heading">
    <div className="route-showcase__intro">
      <h1 className="route-showcase__title" id="route-showcase-heading">
        {heading}
      </h1>
      {subheading && <p className="route-showcase__text">{subheading}</p>}
    </div>

    <MarqueeRow pairs={ROW_ONE} />
    <MarqueeRow pairs={ROW_TWO} reverse />

    <div className="route-showcase__road" aria-hidden="true">
      <Taxi className="route-showcase__road-taxi" />
    </div>
  </section>
)

export default RouteShowcase
